import { Component, OnInit, OnDestroy } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { OrganisationSettingsService } from '../../app/fsl/services/organisation-settings.service';
import { SiteService } from '../../app/fsl/services/context-discovery/site.service';
import { PublishStepper } from '../../context-discovery-services/models/publish-stepper.model';
import { StatusResolver } from '../../context-discovery-services/util';
import { PollingService } from '../../app/fsl/services/context-discovery/polling.service';
import { EventBusService } from '../../app/fsl/services/shared/event-bus.service';
import { HistoryValidationService } from '../../context-discovery-services/services/history-validation.service';
import { LaunchDarklyService } from '../../context-discovery-services/services';
import { FeatureFlags } from '../../context-discovery-services/constants/feature-flags.const';
import { environment } from '../../environments/environment';
import { HistoryConfigStatus } from '../../context-discovery-services/enums/history-config-status';
import { CdtStatus } from '../../app/cdt-status';
import { ViewChild } from '@angular/core';

@Component({
  selector: 'adminapp-asset-discovery-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss']
})
export class LayoutComponent implements OnInit, OnDestroy {
  isContextDiscoveryIsInProgress: boolean;
  isPublishInProgress: boolean;
  isHistoryPublishInProgress: boolean;
  isHistoryValidationInProgress: boolean;
  showLoading: boolean;
  modelId: string;
  showNotFoundError: boolean;
  showAuthenticationError: boolean;
  showAssetIdentificationFailedError: boolean;
  showGenericError: boolean;
  statusResolver: StatusResolver;
  completeOnboardingStatus: PublishStepper[];
  latestSiteStatusResponse: string;
  isSiteData1UploadRequired: boolean;
  isAssetIdentificationRequired: boolean;
  organisationSettings = null;
  organizationId: string;
  isModelPresent: boolean;
  pollingService: PollingService;
  isHistoryValidationFeatureEnabled: boolean;
  isHistoryPublishFeatureEnabled: boolean;
  isAutomaticAnalyticsSetupFeatureEnabled: boolean;
  launchDarklySubscription: any;
  isAssetDiscoveryCompleted: boolean;
  isStartButtonEnabled: boolean;
  contextDiscoveryStatus: string;

  @ViewChild('startDiscoveryButton') startDiscoveryButton: any;

  constructor(private siteService: SiteService, private eventBus: EventBusService, private ref: ChangeDetectorRef,
    private organisationSettingsService: OrganisationSettingsService,
    private historyValidationService: HistoryValidationService,
    private ldService: LaunchDarklyService) {
    this.organizationId = sessionStorage.getItem('organizationid');
    this.organisationSettingsService.setOrganisationId(this.organizationId);
    this.statusResolver = new StatusResolver();
    this.isHistoryValidationFeatureEnabled = this.ldService.getBooleanFeatureFlagValue(FeatureFlags.HISTORY_VALIDATION);
    this.isHistoryPublishFeatureEnabled = this.ldService.getBooleanFeatureFlagValue(FeatureFlags.PUBLISH_HISTORY_DATA);
    this.isAutomaticAnalyticsSetupFeatureEnabled = this.ldService.getBooleanFeatureFlagValue(FeatureFlags.AUTOMATIC_ANALYTICS_SETUP);
    this.launchDarklySubscription = this.ldService.flagChange.subscribe(_ => {
      this.isHistoryValidationFeatureEnabled = this.ldService.
        getBooleanFeatureFlagValue(FeatureFlags.HISTORY_VALIDATION, true);
      this.isHistoryPublishFeatureEnabled = this.ldService.getBooleanFeatureFlagValue(FeatureFlags.PUBLISH_HISTORY_DATA, true);
      this.isAutomaticAnalyticsSetupFeatureEnabled
        = this.ldService.getBooleanFeatureFlagValue(FeatureFlags.AUTOMATIC_ANALYTICS_SETUP, true);
    });
    this.eventBus.serverError$.subscribe((errorMessage) => {
      const identifyAssetsErrorToast: any = document.getElementById('identifyAssetsErrorToast');
      identifyAssetsErrorToast.text = errorMessage;
      identifyAssetsErrorToast.open();
    });
  }

  ngOnDestroy() {
    this.pollingService.stop();
  }


  ngOnInit() {
    this.initializeView();
    this.organisationSettings = null;

    this.pollingService = new PollingService(+environment.cdt.refreshInterval);
    this.pollingService.start(_ => this.siteService.getStatusFromOrganizationId(this.organizationId), statusFromOrgResponse => {

      if (this.isHistoryValidationFeatureEnabled) {
        this.historyValidationService.getValidateHistoryStatus().subscribe(statusResponse => {
          let isHistoryValidationInProgress = false;
          switch (statusResponse.Status) {
            case HistoryConfigStatus.VALIDATION_IN_PROGRESS:
              isHistoryValidationInProgress = true;
              break;
            default:
              isHistoryValidationInProgress = false;
              break;
          }

          if (this.isHistoryValidationInProgress !== isHistoryValidationInProgress) {
            this.isHistoryValidationInProgress = isHistoryValidationInProgress;

            if (!this.isHistoryValidationInProgress) {
              this.processStatusFromOrg(statusFromOrgResponse);
            } else {
              this.showLoading = false;
              this.ref.detectChanges();
            }
          } else if (!this.isHistoryValidationInProgress) {
            this.processStatusFromOrg(statusFromOrgResponse);
          }

        }, this.handleFailure.bind(this));
      } else {
        this.isHistoryValidationInProgress = false;
        this.processStatusFromOrg(statusFromOrgResponse);
      }
    }, this.handleFailure.bind(this));
  }

  processStatusFromOrg(statusResponse) {
    const statusResponseStringified = JSON.stringify(statusResponse);
    if (this.latestSiteStatusResponse !== statusResponseStringified) {
      this.latestSiteStatusResponse = statusResponseStringified;

      this.removeAllErrors();
      this.resetStatusTrackers();

      this.isHistoryPublishInProgress = (this.isHistoryPublishFeatureEnabled
        && this.statusResolver.getStatusOfPublishHistory(statusResponse) === CdtStatus.PUBLISH_IN_PROGRESS);

      this.completeOnboardingStatus = this.statusResolver.getCompleteOnboardingStatus(statusResponse,
        this.isAutomaticAnalyticsSetupFeatureEnabled);

      this.isPublishInProgress = (this.statusResolver.getStatusOfPublishWithAutomaticAnalyticsSetup(this.completeOnboardingStatus,
        this.isAutomaticAnalyticsSetupFeatureEnabled) === CdtStatus.PUBLISH_IN_PROGRESS);

      if (this.isPublishInProgress) {
        this.showLoading = false;
        this.ref.detectChanges();
      } else {
        this.contextDiscoveryStatus = this.statusResolver.getContextDiscoveryStatus(statusResponse);
        switch (this.contextDiscoveryStatus) {
          case CdtStatus.COMPLETED:
          case CdtStatus.PUBLISH_FAILED:
            this.siteService.getModelId(this.organizationId).subscribe(modelIdResponse => {
              this.removeAllErrors();
              if (this.modelId !== modelIdResponse) {
                this.modelId = modelIdResponse;
              }
              this.checkAssetIdentificationRequiredAndShowView();
            }, this.handleFailure.bind(this));
            break;
          case CdtStatus.FAILED:
            this.showAssetIdentificationFailedError = true;
            this.startDiscoveryButton.nativeElement.innerText = 'Retry Discovery';
            this.showLoading = false;
            this.ref.detectChanges();
            break;
          case CdtStatus.IN_PROGRESS:
            this.showLoading = false;
            this.isContextDiscoveryIsInProgress = true;
            this.ref.detectChanges();
            break;
        }
      }
    }
  }


  removeAllErrors() {
    this.showGenericError = false;
    this.showAuthenticationError = false;
    this.showNotFoundError = false;
    this.showAssetIdentificationFailedError = false;
    this.showLoading = true;
  }

  resetStatusTrackers() {
    this.isContextDiscoveryIsInProgress = false;
    this.isPublishInProgress = false;
    this.isHistoryPublishInProgress = false;
    this.isSiteData1UploadRequired = false;
    this.isAssetIdentificationRequired = false;
    this.isHistoryValidationInProgress = false;
    this.isModelPresent = false;
    this.showLoading = true;
  }

  initializeView() {
    this.removeAllErrors();
    this.resetStatusTrackers();
    this.modelId = '';
    this.completeOnboardingStatus = [];
  }

  checkAssetIdentificationRequiredAndShowView() {
    this.organisationSettingsService.getSettings().subscribe(settings => {
      this.organisationSettings = settings;
      this.checkModelPresence(settings);
    }, this.handleFailure.bind(this));
  }


  enableStartButton() {
    if (this.organisationSettings.PreSales.CdtStatus == null) {
      this.isStartButtonEnabled = false;
    }
  }

  checkModelPresence(settings) {
    if (this.modelId) {
      this.siteService.getModelPresenceStatus(this.modelId).subscribe(modelTtlExistenceStatus => {
        this.isModelPresent = modelTtlExistenceStatus;
        if (!modelTtlExistenceStatus) {
          this.showPrerequisiteStepInModelAbsence(settings);
        } else {
          if (this.contextDiscoveryStatus === 'COMPLETED' && settings.PreSales.CdtStatus === 'Triggered' && !this.isPublishInProgress) {
            this.isAssetDiscoveryCompleted = true;
            this.isAssetIdentificationRequired = true;
            this.showLoading = false;
            this.startDiscoveryButton.nativeElement.innerText = 'RESTART DISCOVERY';
          } else {
            this.isAssetIdentificationRequired = true;
          }
          this.showLoading = false;
          this.ref.detectChanges();
        }
      }, this.handleFailure.bind(this));
    } else {
      this.showPrerequisiteStepInModelAbsence(settings);
    }
  }

  showPrerequisiteStepInModelAbsence(settings) {
    if (!settings.PreSales || !settings.PreSales.CdtStatus) {
      this.isSiteData1UploadRequired = true;
    } else if (settings.PreSales && settings.PreSales.CdtStatus === 'NewFile') {
      this.isAssetIdentificationRequired = true;
    }
    this.showLoading = false;
    this.ref.detectChanges();
  }

  startAssetIdentification() {
    this.ngOnDestroy();
    this.initializeView();
    this.ref.detectChanges();
    this.isAssetDiscoveryCompleted = false;
    if (!this.organisationSettings) {
      this.organisationSettingsService.getSettings().subscribe(settings => {
        this.organisationSettings = settings;
        this.identifyAsset();
      }, this.handleFailure.bind(this));
    } else {
      this.identifyAsset();
    }
  }


  identifyAsset() {
    this.organisationSettingsService.setSettings(this.organisationSettings).subscribe(() => {
      this.siteService.createModelId(this.organizationId).subscribe(_ => {
        this.organisationSettings.PreSales.CdtStatus = 'Triggered';
        this.organisationSettingsService.setSettings(this.organisationSettings).subscribe(_ => {
          this.ngOnInit();
        }, this.handleFailure.bind(this));
      }, this.handleFailure.bind(this));
    }, this.handleFailure.bind(this));
  }

  isCdtOperationOngoing() {
    return this.isContextDiscoveryIsInProgress || this.isPublishInProgress || this.isHistoryValidationInProgress;
  }

  isAnyErrorOccurred() {
    return this.showAuthenticationError || this.showNotFoundError || this.showGenericError || this.showAssetIdentificationFailedError;
  }

  canLayoutBeShown() {
    return this.modelId !== '' && !this.isCdtOperationOngoing() && !this.isAnyErrorOccurred() && !this.isSiteData1UploadRequired
      && !this.isAssetIdentificationRequired && this.isModelPresent;
  }

  handleFailure(errorResponse) {
    this.removeAllErrors();
    this.showLoading = false;
    switch (errorResponse.status) {
      case 401:
        this.showAuthenticationError = true;
        break;
      case 404:
        this.showNotFoundError = true;
        break;
      default:
        this.showGenericError = true;
    }
    this.ref.detectChanges();
  }

}
